import React, { Fragment, ReactElement } from 'react';
import {
  Label,
  Listbox,
  Transition,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/react';
import { ChevronUpDownIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { Tooltip, TooltipProps } from '../tooltip';
import { CheckIcon } from '../../atoms/icons';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export interface SelectV2Props<T> {
  options: T[];
  value?: string;
  setValue: (value?: string, selected?: T | null) => void;
  label?: React.ReactNode;
  placeholder?: string;
  noOptionsMessage?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  optionsClassName?: string;
  hideCloseIcon?: boolean;
  errorClass?: string;
  errorMessageClass?: string;
  errorMessage?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  buttonTooltipPlacement?: TooltipProps['placement'];
  selectClass?: string;
  listboxContainerClassName?: string;
  'data-testid'?: string;
}

// Helper to group options by their group property
function groupOptions<T extends { group?: string }>(
  t: TFunction<'designSystem'>,
  options: T[],
) {
  const grouped = options.reduce(
    (acc, option) => {
      const groupKey = option.group || t('other');
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(option);
      return acc;
    },
    {} as Record<string, T[]>,
  );

  return Object.entries(grouped).map(([title, items]) => ({ title, items }));
}

function SelectV2Component<
  T extends {
    key: string;
    label?: string | React.ReactNode;
    group?: string;
    'data-testid'?: string;
  },
>(
  {
    options,
    label,
    placeholder,
    noOptionsMessage = 'No options available',
    setValue,
    wrapperClassName,
    labelClassName,
    optionsClassName,
    hideCloseIcon,
    errorMessage,
    errorClass,
    errorMessageClass,
    selectClass,
    disabled,
    value,
    disabledTooltip = '',
    buttonTooltipPlacement,
    listboxContainerClassName,
    'data-testid': dataTestId,
  }: SelectV2Props<T>,
  ref: React.ForwardedRef<HTMLButtonElement>,
): ReactElement<any> {
  const { t } = useTranslation('designSystem');
  const hasErrors = !!errorMessage;
  const normalFocus = 'focus:border-br-primary focus:ring-br-primary';
  errorClass =
    errorClass !== undefined
      ? errorClass
      : 'border-2 border-rose-600 focus:border-rose-400 focus:ring-rose-200 ';
  const stateClassNames = hasErrors ? errorClass : normalFocus;
  const currentOption = options.find((option) => option.key === value) || null;

  // Check if any option has a group property
  const hasGroups = options.some((option) => option.group);
  const groupedOptions = hasGroups ? groupOptions(t, options) : null;

  return (
    <div className={`${wrapperClassName} flex items-center gap-x-2`}>
      <div className="w-full flex-grow">
        <Listbox
          disabled={disabled}
          value={currentOption}
          onChange={(item) => {
            setValue(item?.key, item);
          }}
        >
          <Label
            className={`block text-sm font-medium text-gray-700 dark:text-gray-300 ${labelClassName}`}
          >
            {label}
          </Label>
          <div
            className={
              listboxContainerClassName
                ? `${listboxContainerClassName}`
                : 'relative mt-1'
            }
          >
            <Tooltip
              label={disabled ? disabledTooltip : ''}
              placement={buttonTooltipPlacement || 'bottom'}
            >
              <ListboxButton
                className={`relative w-full ${
                  disabled
                    ? 'cursor-not-allowed bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    : 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100'
                } cursor-default rounded-md border border-gray-300 dark:border-gray-600 py-2 pr-12 pl-4 text-left shadow-sm focus:ring-1 focus:outline-none sm:text-sm ${stateClassNames} ${selectClass}`}
                ref={ref}
                data-testid={dataTestId}
              >
                {currentOption ? (
                  <>
                    <span className="block truncate">
                      {currentOption.label || currentOption.key}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                      <ChevronUpDownIcon
                        className="h-5 w-5 text-gray-400 dark:text-gray-500"
                        aria-hidden="true"
                      />
                    </span>
                  </>
                ) : (
                  <>
                    <span className="block truncate text-gray-400 dark:text-gray-500">
                      {placeholder}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                      <ChevronUpDownIcon
                        className="text-darkest-gray h-5 w-5"
                        aria-hidden="true"
                      />
                    </span>
                  </>
                )}
              </ListboxButton>
            </Tooltip>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions
                modal={false}
                className={`absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-none sm:text-sm ${optionsClassName}`}
              >
                {options.length === 0 ? (
                  <div className="relative cursor-default py-2 pr-9 pl-3 text-gray-500 dark:text-gray-400 select-none">
                    {noOptionsMessage}
                  </div>
                ) : hasGroups && groupedOptions ? (
                  groupedOptions.map((group) => (
                    <Fragment key={group.title}>
                      <div className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-800 px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-400">
                        {group.title}
                      </div>
                      {group.items.map((option) => (
                        <ListboxOption
                          key={option.key}
                          className={({ focus }) =>
                            `${
                              focus ? 'bg-action-background' : ''
                            } relative z-9 cursor-default py-2 pr-9 pl-3 text-black select-none dark:text-white`
                          }
                          value={option}
                          data-testid={option['data-testid']}
                        >
                          {({ selected }) => (
                            <>
                              <div className="flex">
                                <span
                                  className={`${
                                    selected ? 'font-semibold' : 'font-normal'
                                  } ml-2 truncate`}
                                >
                                  {option.label || option.key}
                                </span>
                              </div>

                              {selected ? (
                                <span className="text-action-primary absolute inset-y-0 right-0 flex items-center pr-4">
                                  <CheckIcon />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </Fragment>
                  ))
                ) : (
                  options.map((option) => (
                    <ListboxOption
                      key={option.key}
                      className={({ focus }) =>
                        `${
                          focus ? 'bg-action-background' : ''
                        } relative cursor-default py-2 pr-9 pl-3 text-black select-none dark:text-white`
                      }
                      value={option}
                      data-testid={option['data-testid']}
                    >
                      {({ selected }) => (
                        <>
                          <div className="flex">
                            <span
                              className={`${
                                selected ? 'font-semibold' : 'font-normal'
                              } ml-2 truncate`}
                            >
                              {option.label || option.key}
                            </span>
                          </div>

                          {selected ? (
                            <span className="text-action-primary absolute inset-y-0 right-0 flex items-center pr-4">
                              <CheckIcon />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))
                )}
              </ListboxOptions>
            </Transition>
          </div>
        </Listbox>
        {errorMessage && (
          <p className={`mt-2 text-sm text-red-400 ${errorMessageClass}`}>
            {errorMessage}
          </p>
        )}
      </div>
      {!hideCloseIcon && (
        <span
          className={`mt-[22px] ml-auto flex cursor-pointer items-center pr-2 ${
            currentOption ? '' : 'pointer-events-none opacity-0'
          }`}
          onClick={() => {
            setValue?.(undefined, undefined);
          }}
        >
          <XCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
        </span>
      )}
    </div>
  );
}

export const SelectV2 = React.forwardRef(SelectV2Component);
